import { useState, useRef, useCallback, useEffect } from "react";
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogContent,
  Icon,
  IconButton,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  useTheme,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import AgGridTable from "../../utilities/AgGridTable";
import ProjectOverview from "./ProjectOverview";
import FormPageHeader from "../../utilities/FormPageHeader";
import { useLocation, useNavigate } from "react-router-dom";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { TabPanel } from "@mui/lab";
import OAuthService from "src/app/auth/services/oAuthService";
import oAuthServiceConfig from "src/app/auth/services/oAuthService/oAuthServiceConfig";
import ProjectOrderSuccess from "./ProjectOrderSuccess";
import { useDispatch } from "react-redux";
import { clearRowDataValue, clearStepData, clearOrderFormData } from "app/store/projectSlice";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box className="h-full">{children}</Box>}
    </div>
  );
}

const newPrjOrderUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.newCustomerProjectOrder}`;
const projectDraftUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.projectDraft}`;
const showFixedValue = (props) => {
  let formattedNumbers;
  if (Number.isNaN(props?.value) || props?.value === 0 || !props?.value) {
    formattedNumbers = "";
  } else {
    if (props.colDef.field === "volume" || props.colDef.field === "weight") {
      formattedNumbers = new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 3,
        minimumFractionDigits: 3,
      }).format(props?.value);
    } else {
      formattedNumbers = new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(props?.value);
    }
  }
  return <p className="text-right">{`${formattedNumbers}`}</p>;
};

const totalListPrice = (props) => {
  const LPWG1 = props?.data?.wg1;
  const LPWG2 = props?.data?.wg2;
  if (LPWG1 && LPWG2) {
    const total = Number(LPWG1) + Number(LPWG2);
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(total);
  }
};

export default function PRJOrderSummary(props) {

  const location = useLocation();
  const dataInUrl = location?.state?.payload;
  const draftId = localStorage.getItem("draftId");
  const isDraftOrder = location?.state?.isDraftOrder;
  const navigate = useNavigate();
  const theme = useTheme();
  const [selectedTab, setSelectedTab] = useState(0);
  const gridRef = useRef();
  const [openSuccessOrder, setOpenSuccessOrder] = useState(false);
  const [currentOrder, setCurrentOrder] = useState([]);
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const [localDraftId, setLocalDraftId] = useState(null);
  const dispatch = useDispatch();

  const [data, setData] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState([]);
  const [overviewColumnDefs, setOverviewColumnDefs] = useState([
    { headerName: "Sno.", valueGetter: "node.rowIndex + 1", maxWidth: 70 },
    {
      field: "typeName", headerName: "TYPE Name", valueFormatter: (params) =>
        params.value ? params.value.toUpperCase() : ""
    },
    {
      field: "uniqId", headerName: "Uniq ID", valueFormatter: (params) =>
        params.value ? params.value.toUpperCase() : ""
    },
    { field: "commissionName", headerName: "Commission Name", minWidth: 225 },
    { field: "volume", cellRenderer: showFixedValue, },
    { field: "weight", cellRenderer: showFixedValue, },
    { field: "wg1", headerName: "List Price WG1", minWidth: 70, cellRenderer: showFixedValue, },
    { field: "wg2", headerName: "List Price WG2", minWidth: 70, cellRenderer: showFixedValue, },
    { field: "totalWGPrice", headerName: "Total List Price", cellRenderer: totalListPrice, },
    { field: "range1", headerName: "Range1", cellStyle: { textAlign: "left" }, },
    { field: "priceGroup1", headerName: "Price Group1", cellStyle: { textAlign: "left" }, },
    { field: "color1", headerName: "Front Color1", cellStyle: { textAlign: "left" }, },
    { field: "carcass1", headerName: "Carcass1", cellStyle: { textAlign: "left" }, },
    { field: "handle1", headerName: "Handle1", cellStyle: { textAlign: "left" }, },
    { field: "range2", headerName: "Range2", cellStyle: { textAlign: "left" }, },
    { field: "priceGroup2", headerName: "Price Group2", cellStyle: { textAlign: "left" }, },
    { field: "color2", headerName: "Front Color2", cellStyle: { textAlign: "left" }, },
    { field: "carcass2", headerName: "Carcass2", cellStyle: { textAlign: "left" }, },
    { field: "handle2", headerName: "Handle2", cellStyle: { textAlign: "left" }, },
    { field: "range3", headerName: "Range3", cellStyle: { textAlign: "left" }, },
    { field: "priceGroup3", headerName: "Price Group3", cellStyle: { textAlign: "left" }, },
    { field: "color3", headerName: "Front Color3", cellStyle: { textAlign: "left" }, },
    { field: "carcass3", headerName: "Carcass3", cellStyle: { textAlign: "left" }, },
    { field: "handle3", headerName: "Handle3", cellStyle: { textAlign: "left" }, },
    { field: "loadingWeek" },
    { field: "freight" },
    { field: "specialComments", headerName: "Comments" },
  ]);


  useEffect(() => {
    if (location.state && !data) {
      setData(location.state.payload);
      setLocalDraftId(location.state.draftId);

      navigate('.', { replace: true, state: null });
    }
  }, [location.state, data, navigate]);

  useEffect(() => {
    if (!dataInUrl && !data) {
      navigate('/order/create-order', { replace: true });
    }
  }, [dataInUrl, data, navigate]);

  useEffect(() => {
    if (data) {
      setSelectedRowData(data.projectTypeInfo ?? []);
    }
  }, [data]);
  const onBtExport = useCallback(() => {
    const params = {
      fileName: 'Project summary.xlsx',
      sheetName: 'Project Summary'
    }
    gridRef.current.api.exportDataAsExcel(params);
  }, []);
  const isBooting = !data;


  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const extendedOrderList = selectedRowData.map((obj) => {
    const { orderList, ...mergeObject } = obj;

    const extendedOrderListItems = obj.orderList?.map((orderListItem) => ({
      ...orderListItem,
      ...mergeObject,
    }));

    return {
      ...obj,
      orderList: extendedOrderListItems,
    };
  });
  const orderList = extendedOrderList.map((list) => list.orderList);
  const mergeOrders = [].concat(...orderList);

  const handlePlaceOrder = async () => {
    try {
      setLoading(true);
      await OAuthService.addData(newPrjOrderUrl, data).then((list) => {
        const data = list.data;
        console.log("PLACE ORDER:", data, list);
        setCurrentOrder(data);
      });
      if (localDraftId || draftId) {
        let id = localDraftId || draftId;
        try {
          await OAuthService.deleteData(projectDraftUrl, id);
          console.log("Draft deleted successfully");
        } catch (deleteError) {
          console.error("Draft deletion failed:", deleteError);
        }
      }
      setOpenSuccessOrder(true);
      dispatch(clearRowDataValue());
      dispatch(clearOrderFormData());
      dispatch(clearStepData());
    } catch (error) {
      setError(error?.response?.data?.errorMessage || error.message);
    } finally {
      setLoading(false);
    }
  };

  const orderHeaderAction = () => {
    // const navigate = useNavigate();
    return (
      <>

        <Button
          variant="contained"
          color="secondary"
          onClick={handlePlaceOrder}
          size="small"
          sx={{ minWidth: "auto" }}
          disabled={loading || openSuccessOrder}
        >
          {loading ? "Placing Order..." : "Place Order"}
        </Button>
      </>
    );
  };

  return (
    <>
      {isBooting ? (
        <div className="flex h-full items-center justify-center">
          <Typography variant="h6"
            sx={{ color: theme.palette.text.primary }}>Loading…</Typography>
        </div>
      ) : (
        <>
          <FormPageHeader
            componentName="Project Summary"
            pageName="Project Summary"
            onBtExport={onBtExport}
            currentPage="Project Summary"
            orderHeaderAction={orderHeaderAction()}
            error={error}
          />

          <div className="h-full">
            <AgGridTable
              rowData={mergeOrders}
              columnDefs={overviewColumnDefs}
              gridRef={gridRef}
              hideCheckbox
              editable={false}
            />
          </div>

          <ProjectOrderSuccess
            open={openSuccessOrder}
            currentOrder={currentOrder}
            setOpenSuccessOrder={setOpenSuccessOrder}
          />
        </>
      )}
    </>
  );
}
